import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCheckIcon, CreditCardIcon, ShipIcon, FileTextIcon, CheckCircleIcon, PlayIcon, StopCircleIcon, BarChart3Icon, ArrowRightIcon } from 'lucide-react';
export function UserFlow() {
  const navigate = useNavigate();
  const steps = [{
    id: 1,
    title: 'Add Beneficiary',
    description: 'Register new resource collector',
    icon: <UserCheckIcon size={32} data-id="element-2923" data-mp="21iia1hhttww8" />,
    role: 'Station Officer',
    path: '/beneficiaries',
    status: 'completed' as const
  }, {
    id: 2,
    title: 'Approve Beneficiary',
    description: 'Review and approve registration',
    icon: <CheckCircleIcon size={32} data-id="element-2924" data-mp="2f83nydgrrlxe" />,
    role: 'Range Officer',
    path: '/beneficiaries',
    status: 'completed' as const
  }, {
    id: 3,
    title: 'Issue Smart ID',
    description: 'Generate QR/RFID card',
    icon: <CreditCardIcon size={32} data-id="element-2925" data-mp="1mqq601nhg59t" />,
    role: 'Station Officer',
    path: '/smart-id',
    status: 'completed' as const
  }, {
    id: 4,
    title: 'Register Vessel',
    description: 'Register boat (if applicable)',
    icon: <ShipIcon size={32} data-id="element-2926" data-mp="192h8xyi1zi0o" />,
    role: 'Station Officer',
    path: '/vessels',
    status: 'current' as const
  }, {
    id: 5,
    title: 'Apply Permit',
    description: 'Submit forest entry permit',
    icon: <FileTextIcon size={32} data-id="element-2927" data-mp="ubqhirkdiky3" />,
    role: 'Beneficiary/Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 6,
    title: 'Approve Permit',
    description: 'Review and approve permit',
    icon: <CheckCircleIcon size={32} data-id="element-2928" data-mp="lb29cdhnvet4" />,
    role: 'Range Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 7,
    title: 'Start Trip',
    description: 'QR/RFID scan at entry',
    icon: <PlayIcon size={32} data-id="element-2929" data-mp="2fm7a9a1d5d4r" />,
    role: 'Field Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 8,
    title: 'End Trip',
    description: 'QR/RFID scan at exit',
    icon: <StopCircleIcon size={32} data-id="element-2930" data-mp="dqrf5o2c7f95" />,
    role: 'Field Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 9,
    title: 'Monitoring & Reporting',
    description: 'Track and analyze data',
    icon: <BarChart3Icon size={32} data-id="element-2931" data-mp="41zqppwhjd2z" />,
    role: 'HQ Admin',
    path: '/reports',
    status: 'upcoming' as const
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 border-green-500 text-green-900';
      case 'current':
        return 'bg-blue-100 border-blue-500 text-blue-900';
      case 'upcoming':
        return 'bg-gray-100 border-gray-300 text-gray-600';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-600';
    }
  };
  return <div className="space-y-6" data-id="element-2932" data-mp="32l6opmlyq9l6">
      <div data-id="element-2933" data-mp="37igbsvlvq95u">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2934" data-mp="37izsk8zsmria">Complete User Flow</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2935" data-mp="1pjsd51x3zqwn">
          End-to-end journey from registration to monitoring
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2936" data-mp="1kkfihuzzxfji">
        <p className="text-sm text-blue-900" data-id="element-2937" data-mp="1yl9sgi2ozoka">
          <strong data-id="element-2938" data-mp="1yivx0843oac7">How to use:</strong> This diagram shows the complete workflow
          from beneficiary registration to trip monitoring. Click on any step to
          navigate to that module.
        </p>
      </div>

      {/* Horizontal Flow */}
      <div className="bg-white rounded-lg border border-gray-200 p-8 overflow-x-auto" data-id="element-2939" data-mp="11vikbg1ha2w0">
        <div className="flex items-center gap-4 min-w-max" data-id="element-2940" data-mp="1fmnp0wjpdld7">
          {steps.map((step, idx) => <Fragment key={step.id} data-id="element-2941" data-mp="1auvwwhkwf7ky" data-mpi={idx}>
              <button onClick={() => navigate(step.path)} className={`flex-shrink-0 w-64 border-2 rounded-lg p-6 text-left transition-all hover:shadow-lg ${getStatusColor(step.status)}`} data-id="element-2942" data-mp="1atuko9s5cf1n">
                <div className="flex items-start gap-4 mb-3" data-id="element-2943" data-mp="j2exadirmoad">
                  <div className={`p-3 rounded-lg ${step.status === 'completed' ? 'bg-green-200' : step.status === 'current' ? 'bg-blue-200' : 'bg-gray-200'}`} data-id="element-2944" data-mp="vv1sozc8i52g">
                    {step.icon}
                  </div>
                  <div className="flex-1" data-id="element-2945" data-mp="1ai3fti30rpyz">
                    <div className="text-xs font-semibold uppercase mb-1" data-id="element-2946" data-mp="1ajxx8mf30zyb">
                      Step {step.id}
                    </div>
                    <h3 className="font-bold text-lg mb-1" data-id="element-2947" data-mp="1jj1j1y5mvtgi">{step.title}</h3>
                  </div>
                </div>
                <p className="text-sm mb-3" data-id="element-2948" data-mp="1ohmlm48cyp84">{step.description}</p>
                <div className="flex items-center justify-between" data-id="element-2949" data-mp="1ogbg9zz9l9ew">
                  <span className="text-xs font-medium" data-id="element-2950" data-mp="1tgpdso38p0yo">{step.role}</span>
                  {step.status === 'completed' && <CheckCircleIcon size={20} className="text-green-600" data-id="element-2951" data-mp="3qbu7uby1m1td" />}
                  {step.status === 'current' && <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" data-id="element-2952" data-mp="3npbg3agzp6ru"></div>}
                </div>
              </button>
              {idx < steps.length - 1 && <ArrowRightIcon className="flex-shrink-0 text-gray-400" size={32} data-id="element-2953" data-mp="3dygsa1o3hz4a" />}
            </Fragment>)}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2954" data-mp="ai1bq39k4wkb">
        <h3 className="font-semibold text-gray-900 mb-4" data-id="element-2955" data-mp="5khvwyx7obq9">Status Legend</h3>
        <div className="flex gap-6" data-id="element-2956" data-mp="k3mpdjvivqn">
          <div className="flex items-center gap-2" data-id="element-2957" data-mp="j27cwvtrn34">
            <div className="w-4 h-4 bg-green-500 rounded" data-id="element-2958" data-mp="3rqwxn94gy98d"></div>
            <span className="text-sm text-gray-700" data-id="element-2959" data-mp="3rqetlqbkeqpw">Completed</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2960" data-mp="2lbdxnkmji8jb">
            <div className="w-4 h-4 bg-blue-500 rounded" data-id="element-2961" data-mp="2gbrhb2bf0c6a"></div>
            <span className="text-sm text-gray-700" data-id="element-2962" data-mp="2ga6mdbl6ur7w">Current Step</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2963" data-mp="18ckb2ocvybb4">
            <div className="w-4 h-4 bg-gray-300 rounded" data-id="element-2964" data-mp="1cde3nv19weac"></div>
            <span className="text-sm text-gray-700" data-id="element-2965" data-mp="1cbjpr0of5mfr">Upcoming</span>
          </div>
        </div>
      </div>
    </div>;
}